import type { CatalogueItem } from '@/helpers/promotion-helper';
import type { WebLocale } from '@/enums/web-locale';
import type { Maybe } from '@/helpers/types';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { UnwrapRef } from 'vue';

import { computed } from 'vue';
import { toValue } from '@vueuse/core';

import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { getAllRawCatalogue, getRawCatalogue } from '@/helpers/promotion-helper';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import Provider from '@/interfaces/provider';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

interface UsePromotionOptions {
	offers: MaybeRefOrGetter<TitleOfferFragment[]>;
	title: MaybeRefOrGetter<TitleDetail | undefined>;
	countryOrGeoCountry?: MaybeRefOrGetter<Maybe<string>>;
	hardcodedShortName?: MaybeRefOrGetter<string | undefined | null>;
}

export type UsePromotion = UnwrapRef<ReturnType<typeof usePromotion>>;

/** Only until we get the remaining translations. */
export const allowedPromotionLanguages = [
	'es',
	'en',
	'de',
	'fr',
	'pt',
	'it',
	'sv',
	'pl',
	'bg',
	'cs',
	'fi',
	'hu',
	'ko',
	'tr',
	'az',
	'el',
	'ja',
	'ro',
	'ru',
	'sr',
	'uk',
	'ur',
	'ar',
];

/** Individual titles on which to hide the promotion row for a campaign. */
const campaignTitles: Array<{ country: Uppercase<WebLocale>; id: `${'tm' | 'ts' | 'tss'}${string}` }> = [
	{ country: 'DE', id: 'ts300008' }, // /de/Serie/are-you-the-one-reality-stars-in-love
	{ country: 'DE', id: 'ts345689' }, // /de/Serie/neue-geschichten-vom-pumuckl
	{ country: 'DE', id: 'ts22060' }, // /de/Serie/Sex-and-the-City
	{ country: 'DE', id: 'ts2' }, // /de/Serie/Game-of-Thrones-Das-Lied-von-Eis-und-Feuer
	{ country: 'DE', id: 'tss360333' }, // /de/Serie/Yellowstone-2018/Staffel-5
] as const;

/**
 * Record of titles, and for which countries the promotion row should be hidden.
 */
const hiddenPromotionTitles: Record<string, string[]> = { tm453584: ['DE', 'AT', 'CH'] };

export function usePromotion({ offers, title, countryOrGeoCountry, hardcodedShortName }: UsePromotionOptions) {
	const { geoSwiperPromotionCatalogue, isGeoSwiperCountry, geoSwiperOfferFilter } = useGeoSwiper({ title });

	const { country: countryFromLocale } = storeToRefs(useLanguageStore());
	const { allProvidersByShortName } = storeToRefs(useConstantStore());
	const { activeRoute } = storeToRefs(useRoutingStore());

	const _title = computed(() => toValue(title));
	const country = computed(() => toValue(countryOrGeoCountry) ?? countryFromLocale.value);
	const hardcodedOfferShortName = computed(() => toValue(hardcodedShortName));
	const isLocalisedPromotion = computed(() => country.value !== countryFromLocale.value);

	/** Campaigns are being added to individual titles, where we are requested to hide the promotion row. */
	const isCampaignTitle = computed(() => {
		if (!activeRoute.value?.name?.includes('app.detail')) return false;

		const campaignTitle = campaignTitles.find(
			({ id, country }) => id === _title.value?.id && country === countryFromLocale.value
		);

		if (campaignTitle != null) return true;

		return (hiddenPromotionTitles[_title.value?.id || ''] || []).includes(toValue(countryFromLocale));
	});

	const promotionCatalogue = computed(() => {
		if (isGeoSwiperCountry.value && isLocalisedPromotion.value)
			return geoSwiperPromotionCatalogue(country, isLocalisedPromotion);

		if (!_title.value) {
			return new Map<string, CatalogueItem>();
		}

		return new Map<string, CatalogueItem>(
			getAllRawCatalogue({ country, title: _title.value })
				.filter(item => !!allProvidersByShortName.value[item.shortName as keyof Provider])
				.map(item => [item.shortName, item])
		);
	});

	const availableOffers = computed(() => toValue(offers));

	/** The highest priority title offer from the promotion catalogue based on the catalogue's priorities. */
	const bestTitleOffer = computed(() => {
		if (isCampaignTitle.value || !_title.value) return null;

		// GeoLocalised promotions
		if (hardcodedOfferShortName.value) {
			const bestOffer = getRawCatalogue({ country, title: _title.value })
				.filter(
					item =>
						geoSwiperOfferFilter(item, isLocalisedPromotion, hardcodedOfferShortName.value) ||
						!!allProvidersByShortName.value[item.shortName as keyof Provider]
				)
				.map(item => availableOffers.value.find(offer => offer.package.shortName === item.shortName))
				.find(offer => offer?.package.shortName === hardcodedOfferShortName.value);

			return bestOffer ?? null;
		}
		// GeoLocalised promotions

		const bestOffer = Array.from(promotionCatalogue.value.keys())
			.map(shortName => availableOffers.value.find(offer => offer.package.shortName === shortName))
			.find(offer => offer != null);

		return bestOffer ?? null;
	});

	/** If the title has an offer from a provider in the promotion catalogue. */
	const inPromotionCatalogue = computed(() => bestTitleOffer.value !== null);

	/**
	 * The promotion that matches the highest priority offer from the catalogue.
	 * If no match is found, returns the fallback promotion; currently Apple TV+.
	 */
	const catalogueItem = computed(() => {
		if (isCampaignTitle.value || !_title.value) return null;
		//GeoLocalised promotions
		if (hardcodedOfferShortName.value) {
			const hardcodedFallbackOffer = getRawCatalogue({ country, title: _title.value })
				.filter(
					item =>
						geoSwiperOfferFilter(item, isLocalisedPromotion, hardcodedOfferShortName.value) ||
						!!allProvidersByShortName.value[item.shortName as keyof Provider]
				)
				.find(item => item.shortName === hardcodedOfferShortName.value);

			if (hardcodedFallbackOffer) {
				return hardcodedFallbackOffer;
			}
		}
		//GeoLocalised promotions

		if (bestTitleOffer.value) {
			return promotionCatalogue.value.get(bestTitleOffer.value.package.shortName) ?? null;
		}

		const { done, value } = promotionCatalogue.value.values().next();

		if (!done) return value;

		return null;
	});

	/** The top offer from the promotion catalogue. */
	const promotionOffer = computed(() => {
		if (catalogueItem.value) {
			return catalogueItem.value.offer(bestTitleOffer, country, inPromotionCatalogue);
		}

		return bestTitleOffer.value;
	});

	return {
		/** All the promotions that should be included, sorted by priority. */
		promotionCatalogue,

		/** If the title has an offer from a provider in the promotion catalogue. */
		inPromotionCatalogue,

		/** The highest priority title offer from the promotion catalogue based on the catalogue's priorities. */
		bestTitleOffer,

		/**
		 * The promotion that matches the highest priority offer from the catalogue.
		 * If no match is found, returns the fallback promotion; currently Apple TV+.
		 */
		catalogueItem,

		/** The top offer from the promotion catalogue. */
		promotionOffer,
	};
}
